/**
 * PhysicsScriptApi — the `engine.physics` capability module (global service).
 *
 * The first real `@scriptApi` declaration: coarse physics queries + force application (NOT a
 * per-entity-per-frame path, per design §6). Its concrete backing is injected per runtime (DI)
 * — the player wires a native/Rapier-backed impl, the editor a play-mode impl, tests a fake —
 * so the module stays dependency-free in core. Collision *events* are not delivered here; they
 * arrive via `IScriptExecutor.triggerEvent`, symmetrically to every backend.
 *
 * The backing's query methods (raycast/overlapCircle) are required; the mutating methods
 * (applyForce/…) are OPTIONAL so a read-only backing (e.g. the legacy `PhysicsCaps`) can serve
 * as a partial backing — the module no-ops the missing ones rather than throwing.
 *
 * Design ref: `.refactor/SCRIPTING-API-DESIGN.md` §4 (declaration example) + §11 (this is
 * where the current `PhysicsCaps` surface migrates to).
 */

import { scriptApi, scriptFn } from "../scriptApi";
import type { EntityHandle, RaycastHit, Vec2Like } from "../IEngineContext";

/** The injected backing the physics module resolves against (DI per runtime). */
export interface PhysicsBacking {
  // --- queries (required) ---
  raycast(origin: Vec2Like, direction: Vec2Like, maxDistance?: number): RaycastHit | null;
  overlapCircle(center: Vec2Like, radius: number): readonly EntityHandle[];
  // --- mutations (optional; a read-only backing omits these) ---
  applyForce?(entity: EntityHandle, fx: number, fy: number): void;
  applyImpulse?(entity: EntityHandle, ix: number, iy: number): void;
  setVelocity?(entity: EntityHandle, vx: number, vy: number): void;
  getVelocity?(entity: EntityHandle): Vec2Like;
}

const ZERO: Vec2Like = { x: 0, y: 0 };

@scriptApi("physics", { tier: "safe", version: 1 })
export class PhysicsScriptApi {
  constructor(private readonly _backing: PhysicsBacking) {}

  @scriptFn() raycast(
    origin: Vec2Like,
    direction: Vec2Like,
    maxDistance?: number,
  ): RaycastHit | null {
    return this._backing.raycast(origin, direction, maxDistance);
  }

  @scriptFn() overlapCircle(center: Vec2Like, radius: number): readonly EntityHandle[] {
    return this._backing.overlapCircle(center, radius);
  }

  @scriptFn() applyForce(entity: EntityHandle, fx: number, fy: number): void {
    this._backing.applyForce?.(entity, fx, fy);
  }

  @scriptFn() applyImpulse(entity: EntityHandle, ix: number, iy: number): void {
    this._backing.applyImpulse?.(entity, ix, iy);
  }

  @scriptFn() setVelocity(entity: EntityHandle, vx: number, vy: number): void {
    this._backing.setVelocity?.(entity, vx, vy);
  }

  @scriptFn() getVelocity(entity: EntityHandle): Vec2Like {
    return this._backing.getVelocity?.(entity) ?? ZERO;
  }
}
